<template>
  <Dialog
    :is-open="isOpen"
    title="Task Details"
    width="500px"
    :key="dialogKey"
    @close="close"
  >
    <div :class="$style.detailSection">
      <h3>Basic Information</h3>
      <div :class="$style.detailRow">
        <span :class="$style.label">Name:</span>
        <div :class="$style.editableField">
          <input
            v-if="isEditingName"
            v-model="editingName"
            :class="$style.nameInput"
            @keyup.enter="saveName"
            @blur="saveName"
            ref="nameInput"
          />
          <span v-else :class="$style.nameDisplay" @click="startEditingName">
            {{ currentTask.name }}
          </span>
        </div>
      </div>
      <div :class="$style.detailRow">
        <span :class="$style.label">Topic:</span>
        <div :class="$style.editableField">
          <TopicSelect
            :model-value="currentTask.topicId"
            @update:modelValue="updateTopic"
          />
        </div>
      </div>
      <div :class="$style.detailRow">
        <span :class="$style.label">Project:</span>
        <div :class="$style.editableField">
          <ProjectSelect
            ref="projectSelectRef"
            :model-value="currentTask.projectId"
            @update:modelValue="updateProject"
          />
        </div>
      </div>
      <div :class="$style.detailRow">
        <span :class="$style.label">Status:</span>
        <span :class="$style.value">{{
          currentTask.completed ? "Completed" : "In Progress"
        }}</span>
      </div>
    </div>

    <div :class="$style.detailSection">
      <h3>Time Information</h3>
      <div :class="$style.detailRow">
        <span :class="$style.label">Elapsed Time:</span>
        <span :class="$style.value">{{
          formatDuration(currentTask.duration)
        }}</span>
      </div>
      <div :class="$style.detailRow">
        <span :class="$style.label">Estimated Time:</span>
        <div :class="$style.editableField">
          <TimeInput
            :duration="currentTask.estimatedDuration"
            @update:duration="updateEstimatedDuration"
          />
        </div>
      </div>
      <!-- Remove the separate last session edit field -->
    </div>

    <div
      v-if="currentTask.sessions && currentTask.sessions.length > 0"
      :class="$style.detailSection"
    >
      <div :class="$style.sessionsHeader">
        <h3>Work Sessions ({{ currentTask.sessions.length }})</h3>
        <button
          :class="$style.moveSessionsButton"
          @click="showMoveSessionsDialog = true"
          title="Move sessions to new task"
        >
          <MoveIcon :size="16" />
          Move Sessions
        </button>
      </div>
      <div :class="$style.sessionsList">
        <div
          v-for="(session, index) in currentTask.sessions"
          :key="index"
          :class="$style.sessionItem"
        >
          <div :class="$style.sessionHeader">
            <span :class="$style.sessionNumber">Session {{ index + 1 }}</span>
            <span :class="$style.sessionDuration">
              {{ formatSessionDuration(session) }}
            </span>
            <button
              :class="$style.deleteSessionButton"
              @click="deleteSession(index)"
              title="Delete session"
            >
              <DeleteIcon :size="16" />
            </button>
          </div>
          <div :class="$style.sessionTimes">
            <div :class="$style.timeContainer">
              Start:
              <!-- Make all session start times editable -->
              <input
                type="datetime-local"
                :value="formatDateTimeLocal(session.startTime)"
                @change="(event) => updateSessionStartTime(index, event)"
                :class="$style.timeInput"
              />
            </div>
            <div v-if="session.endTime" :class="$style.timeContainer">
              End:
              <!-- Make all session end times editable -->
              <input
                type="datetime-local"
                :value="formatDateTimeLocal(session.endTime)"
                @change="(event) => updateSessionEndTime(index, event)"
                :class="$style.timeInput"
              />
            </div>
            <div v-else :class="$style.activeSession">Active</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isOverrunTask" :class="$style.detailSection">
      <h3>Overrun Reasons</h3>
      <div :class="$style.reasonChips">
        <button
          v-for="reason in availableReasons"
          :key="reason.id"
          :class="[
            $style.reasonChip,
            { [$style.selected]: localOverrunReasons.includes(reason.id) },
          ]"
          @click="toggleOverrunReason(reason.id)"
        >
          {{ reason.label }}
        </button>
      </div>
    </div>

    <!-- Move Sessions Dialog -->
    <MoveSessionsDialog
      :is-open="showMoveSessionsDialog"
      :task="currentTask"
      @close="showMoveSessionsDialog = false"
      @move-sessions="handleMoveSessions"
    />
  </Dialog>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, computed } from "vue";
import type { Task, WorkSession } from "../types/task";
import Dialog from "./Dialog.vue";
import TopicSelect from "./TopicSelect.vue";
import ProjectSelect from "./ProjectSelect.vue";
import DeleteIcon from "./DeleteIcon.vue";
import TimeInput from "./TimeInput.vue";
import MoveSessionsDialog from "./MoveSessionsDialog.vue";
import { MoveIcon } from "./icons";
import { overrunReasonService } from "../services/overrunReasonService";
import { OverrunThreshold } from "../types/enums";
import { isTaskActive } from "../services/taskService";

const props = defineProps<{
  isOpen: boolean;
  task: Task;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:topic", topicId: string): void;
  (e: "update:project", projectId?: string): void;
  (e: "update:estimatedDuration", duration: number): void;
  (e: "update:name", name: string): void;
  (e: "delete-session", sessionIndex: number): void;
  (e: "update:overrunReasons", reasons: string[]): void;
  (
    e: "move-sessions",
    data: {
      sessionIndices: number[];
      newTaskName: string;
      newTaskTopicId: string;
      newTaskEstimatedDuration: number;
    }
  ): void;
  (e: "update:sessionStartTime", sessionIndex: number, startTime: string): void;
  (e: "update:sessionEndTime", sessionIndex: number, endTime: string): void;
}>();

const editingName = ref(props.task.name);
const nameInput = ref<HTMLInputElement | null>(null);
const isEditingName = ref(false);
const projectSelectRef = ref<InstanceType<typeof ProjectSelect> | null>(null);

// Overrun reasons
const availableReasons = overrunReasonService.getReasons();

// Local copy of overrun reasons for editing
const localOverrunReasons = ref<string[]>([]);

// Get the current task state from the queue service
const currentTask = computed(() => {
  return props.task;
});

// Force refresh when dialog opens by using a key
const dialogKey = computed(() => {
  // This will force the dialog to re-render when the task state changes
  return `${props.task.id}-${isTaskActive(props.task)}-${
    props.task.sessions?.length || 0
  }-${props.task.projectId || "no-project"}`;
});

// Watch for dialog open/close to force reactivity
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      // Force reactivity by triggering a change
      isEditingName.value = false;
      editingName.value = currentTask.value.name;
      // Sync local overrun reasons with task
      localOverrunReasons.value = [...(currentTask.value.overrunReasons || [])];
    }
  }
);

// Check if this is an overrun task (completed task that took longer than estimated)
const isOverrunTask = computed(() => {
  return (
    currentTask.value.completed &&
    currentTask.value.estimatedDuration > 0 &&
    currentTask.value.duration >
      currentTask.value.estimatedDuration * OverrunThreshold.PERCENTAGE
  );
});

// Show Move Sessions Dialog
const showMoveSessionsDialog = ref(false);

// Update editing name when task changes
watch(
  () => currentTask.value.name,
  (newName) => {
    editingName.value = newName;
  }
);

// Watch for changes in task overrun reasons
watch(
  () => currentTask.value.overrunReasons,
  (newReasons) => {
    localOverrunReasons.value = [...(newReasons || [])];
  },
  { immediate: true }
);

function formatDuration(ms: number): string {
  if (!ms) return "0h 0m 0s";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  let result = "";
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (minutes > 0 || hours > 0) {
    result += `${minutes}m `;
  }
  if (seconds > 0 || (minutes === 0 && hours === 0)) {
    result += `${seconds}s`;
  }

  return result.trim();
}

function formatSessionDuration(session: WorkSession): string {
  if (!session.endTime) {
    const currentDuration =
      new Date().getTime() - new Date(session.startTime).getTime();
    return formatDuration(currentDuration);
  }
  const duration =
    new Date(session.endTime).getTime() - new Date(session.startTime).getTime();
  return formatDuration(duration);
}

function formatDateTimeLocal(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function updateTopic(newTopicId: string) {
  emit("update:topic", newTopicId);
}

function updateProject(newProjectId?: string) {
  // Always ensure project is created/selected if user typed a project name
  // This handles the case where user types a name but doesn't explicitly create/select it
  let finalProjectId = newProjectId;

  if (projectSelectRef.value) {
    const ensuredProjectId = projectSelectRef.value.ensureProjectCreated();
    // If ensureProjectCreated returned a value, use it (user typed something)
    // Otherwise, use the passed newProjectId (user selected/cleared)
    if (ensuredProjectId !== undefined) {
      finalProjectId = ensuredProjectId;
    }
    // If both are undefined, finalProjectId stays undefined (user cleared)
  }

  emit("update:project", finalProjectId);
}

function updateEstimatedDuration(newDuration: number) {
  emit("update:estimatedDuration", newDuration);
}

function startEditingName() {
  isEditingName.value = true;
  editingName.value = currentTask.value.name;
  nextTick(() => {
    nameInput.value?.focus();
    nameInput.value?.select();
  });
}

function saveName() {
  const newName = editingName.value.trim();
  if (newName && newName !== currentTask.value.name) {
    emit("update:name", newName);
  }
  isEditingName.value = false;
}

function deleteSession(index: number) {
  emit("delete-session", index);
}

function close() {
  emit("close");
}

function updateSessionStartTime(sessionIndex: number, event: Event) {
  console.log(
    "TaskDetailsDialog: updateSessionStartTime called with sessionIndex:",
    sessionIndex
  );
  const input = event.target as HTMLInputElement;
  const newStartTime = new Date(input.value);
  const session = currentTask.value.sessions[sessionIndex];
  const endTime = session.endTime ? new Date(session.endTime) : null;

  console.log(
    "TaskDetailsDialog: newStartTime:",
    newStartTime,
    "session:",
    session
  );

  // Validate that start time is not after end time (if end time exists)
  if (endTime && newStartTime >= endTime) {
    alert("Start time cannot be after or equal to end time");
    input.value = formatDateTimeLocal(session.startTime);
    return;
  }

  // Validate against previous session's end time
  if (sessionIndex > 0) {
    const previousSession = currentTask.value.sessions[sessionIndex - 1];
    if (previousSession.endTime) {
      const previousEndTime = new Date(previousSession.endTime);
      if (newStartTime <= previousEndTime) {
        alert(
          "Start time cannot be before or equal to the previous session's end time"
        );
        input.value = formatDateTimeLocal(session.startTime);
        return;
      }
    }
  }

  const startTime = newStartTime.toISOString();
  console.log("TaskDetailsDialog: emitting update:sessionStartTime with:", {
    sessionIndex,
    startTime,
  });
  emit("update:sessionStartTime", sessionIndex, startTime);
}

function updateSessionEndTime(sessionIndex: number, event: Event) {
  console.log(
    "TaskDetailsDialog: updateSessionEndTime called with sessionIndex:",
    sessionIndex
  );
  const input = event.target as HTMLInputElement;
  const newEndTime = new Date(input.value);
  const session = currentTask.value.sessions[sessionIndex];
  const startTime = new Date(session.startTime);

  console.log(
    "TaskDetailsDialog: newEndTime:",
    newEndTime,
    "session:",
    session
  );

  // Validate that end time is not before start time
  if (newEndTime <= startTime) {
    alert("End time cannot be before or equal to start time");
    const originalEndTime = session.endTime || new Date().toISOString();
    input.value = formatDateTimeLocal(originalEndTime);
    return;
  }

  // Validate against next session's start time
  if (sessionIndex < currentTask.value.sessions.length - 1) {
    const nextSession = currentTask.value.sessions[sessionIndex + 1];
    const nextStartTime = new Date(nextSession.startTime);
    if (newEndTime >= nextStartTime) {
      alert(
        "End time cannot be after or equal to the next session's start time"
      );
      const originalEndTime = session.endTime || new Date().toISOString();
      input.value = formatDateTimeLocal(originalEndTime);
      return;
    }
  }

  const endTime = newEndTime.toISOString();
  console.log("TaskDetailsDialog: emitting update:sessionEndTime with:", {
    sessionIndex,
    endTime,
  });
  emit("update:sessionEndTime", sessionIndex, endTime);
}

function toggleOverrunReason(reasonId: string) {
  let newReasons: string[];

  if (localOverrunReasons.value.includes(reasonId)) {
    // Remove the reason
    newReasons = localOverrunReasons.value.filter((id) => id !== reasonId);
  } else {
    // Add the reason
    newReasons = [...localOverrunReasons.value, reasonId];
  }

  localOverrunReasons.value = newReasons;
  emit("update:overrunReasons", newReasons);
}

function handleMoveSessions(data: {
  sessionIndices: number[];
  newTaskName: string;
  newTaskTopicId: string;
  newTaskEstimatedDuration: number;
}) {
  emit("move-sessions", data);
  showMoveSessionsDialog.value = false;
}
</script>

<style module>
.detailSection {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 15px;
  padding-top: 10px;
}

.detailSection:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detailSection h3 {
  margin: 0 0 10px 0;
  font-size: 1em;
  color: rgba(255, 255, 255, 0.9);
}

.detailRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.detailRow:last-child {
  margin-bottom: 0;
}

.label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
}

.value {
  font-weight: 600;
  color: white;
}

.editableField {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  justify-content: flex-end;
}

.editableField :global(.topicSelect) {
  min-width: 120px;
}

.editableField :global(.timeDisplayContainer) {
  margin-left: 0;
}

.nameInput {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 6px 8px;
  color: white;
  font-size: inherit;
  font-weight: 600;
  font-family: inherit;
  min-width: 150px;
}

.nameInput:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.nameDisplay {
  font-weight: 600;
  color: white;
  cursor: pointer;
  padding: 6px 0px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  display: inline-block;
}

.nameDisplay:hover {
  background: rgba(255, 255, 255, 0.1);
}

.sessionsHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.moveSessionsButton {
  background-color: transparent;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9em;
  padding: 6px 12px;
  border-radius: 4px;
  margin: 0 0 10px 0;
}

.moveSessionsButton:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sessionsList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sessionItem {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 12px;
  border-left: 3px solid var(--primary-color);
}

.sessionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.sessionNumber {
  font-weight: 600;
  color: var(--primary-color);
}

.sessionDuration {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.sessionTimes {
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
}

.activeSession {
  color: var(--primary-color);
  font-weight: 500;
}

.deleteSessionButton {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  margin-left: 8px;
  transition: background 0.2s;
}
.deleteSessionButton:hover {
  background: rgba(255, 0, 0, 0.1);
}

.timeContainer {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
}

.timeInput {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 6px 8px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  width: 80%;
  margin-top: 0.25rem;
}

.timeInput:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

.reasonChips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.reasonChip {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 6px 12px;
  color: white;
  font-size: 0.9em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reasonChip:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.reasonChip.selected {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}
</style>
